import React, { useState } from 'react';
import _orderBy from 'lodash/orderBy';
import { transparentize } from 'polished';
import makeStyles from '@material-ui/core/styles/makeStyles';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

import PersonsListByDateForm from './PersonsListByDateForm';
import PersonsListByDatePresetSelect from './PersonsListByDatePresetSelect';
import PersonsListByDateDeleteConfirmation from './PersonsListsByDateDeleteConfirmation';

import { PersonListsByDatePersonList } from 'pages/Homepage/typings';
import { DEFAULT_PERSON_LISTS_BY_DATE_CONTENT } from 'pages/Homepage/helpers/consts';

interface Props {
  values: any;
  errors: any;
  onSetFieldValue: (fieldKey: string, fieldValue: any) => void;
}

function PersonListsByDateWidgetSettings({ values, errors, onSetFieldValue }: Props) {
  const classes = useStyles();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogIndex, setDeleteDialogIndex] = useState(-1);

  const personLists = values.settings.personLists as Array<PersonListsByDatePersonList>;
  const disableAddList = personLists.length > 2;
  const disableDeleteList = personLists.length === 1;

  const handleAddList = () => {
    const newList = {
      _onPresetSelectStep: true,
      _targetListIndex: personLists.length,
    };

    onSetFieldValue('settings.personLists', [...personLists, newList]);
  };

  const handleReorderLists = (currentTargetIndex: number, newTargetIndex: number) => {
    const currentListRealIndex = personLists.findIndex(
      list => list._targetListIndex === currentTargetIndex,
    );
    const _currentList = personLists[currentListRealIndex];

    const ORIGINAL_INDEX_KEY = '_originalIndex';
    const currentList = { ..._currentList, [ORIGINAL_INDEX_KEY]: currentListRealIndex };
    const currentHiddenLists = _orderBy(
      personLists.map((l, i) => ({
        ...l,
        [ORIGINAL_INDEX_KEY]: i,
      })),
      '_targetListIndex',
    );

    const currentHiddenListsWithoutCurrentList = currentHiddenLists.filter(
      (l, i) => i !== currentTargetIndex,
    );

    const newHiddenLists = [
      ...currentHiddenListsWithoutCurrentList.slice(0, newTargetIndex),
      currentList,
      ...currentHiddenListsWithoutCurrentList.slice(newTargetIndex),
    ];

    const newLists = _orderBy(
      newHiddenLists.map((l, i) => ({
        ...l,
        _targetListIndex: i,
      })),
      ORIGINAL_INDEX_KEY,
    );

    onSetFieldValue('settings.personLists', newLists);
  };

  const handleDeleteList = (index: number) => {
    setDeleteDialogOpen(false);

    if (personLists.length > 1) {
      const newList = personLists.filter((_list, i: number) => i !== index);

      onSetFieldValue(
        'settings.personLists',
        newList.map((list: PersonListsByDatePersonList, i: number) => ({
          ...list,
          _targetListIndex: i,
        })),
      );
    } else {
      onSetFieldValue('settings.personLists', [DEFAULT_PERSON_LISTS_BY_DATE_CONTENT]);
    }
  };

  const handleDeleteConfirmationOpen = (index: number) => {
    setDeleteDialogOpen(true);
    setDeleteDialogIndex(index);
  };

  const handleDeleteConfirmationCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <div>
        <Card>
          <CardContent>
            <div>
              <div>
                <div className={classes.header}>
                  <Typography variant="h6">Lists (3 Max)</Typography>
                  <Tooltip
                    arrow
                    placement="top"
                    title={personLists.length === 3 ? 'Only 3 lists may be added' : ''}
                  >
                    <div>
                      <Button onClick={handleAddList} disabled={disableAddList} color="secondary">
                        + Add List
                      </Button>
                    </div>
                  </Tooltip>
                </div>

                <Typography variant="subtitle2">
                  Add up to three date-based lists to this single widget. If there is more than one
                  list, they will appear as tabs within the widget.
                </Typography>
              </div>
              <div className={classes.listDisplayContainer}>
                <div>
                  {_orderBy(personLists, '_targetListIndex').map((list, index: number) => (
                    <Box className={classes.listDisplayRoot}>
                      <Typography className={classes.listDisplayPosition} variant="body1">
                        List #{index + 1}
                        {list.name ? ':' : ''}{' '}
                      </Typography>
                      <Typography className={classes.listDisplayName} variant="body1">
                        {list.name}
                      </Typography>
                    </Box>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {personLists.map((list: PersonListsByDatePersonList, index: number) => {
          return (
            <div>
              {list._onPresetSelectStep ? (
                <PersonsListByDatePresetSelect
                  list={list}
                  listIndex={index}
                  disableDelete={disableDeleteList}
                  setFieldValue={onSetFieldValue}
                  onDeleteList={handleDeleteConfirmationOpen}
                />
              ) : (
                <PersonsListByDateForm
                  errors={errors}
                  values={values}
                  listIndex={index}
                  setFieldValue={onSetFieldValue}
                  onReorderList={handleReorderLists}
                  onDeleteList={handleDeleteConfirmationOpen}
                />
              )}
            </div>
          );
        })}
      </div>
      <PersonsListByDateDeleteConfirmation
        open={deleteDialogOpen}
        onDelete={handleDeleteList}
        onCancel={handleDeleteConfirmationCancel}
        listIndex={deleteDialogIndex}
      />
    </>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  listDisplayContainer: {
    marginTop: theme.spacing(),
  },
  listDisplayRoot: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxHeight: 30,
    backgroundColor: transparentize(0.92, theme.palette.secondary.main),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(),
    padding: theme.spacing(),
  },
  listDisplayPosition: {
    marginRight: theme.spacing(0.5),
  },
  listDisplayName: {
    fontWeight: theme.typography.fontWeightSemiBold,
  },
}));

export default PersonListsByDateWidgetSettings;
